import React, { useState, useEffect } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { Brain, RefreshCw, Loader2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface BoardProfilePopoverProps {
  boardId: string;
  boardName: string;
  onClose: () => void;
  position: { x: number; y: number };
}

interface BoardProfile {
  semantic_summary: string | null;
  key_topics: string[];
  item_count: number;
  last_profiled_at: string | null;
}

export function BoardProfilePopover({ boardId, boardName, onClose, position }: BoardProfilePopoverProps) {
  const { boardProfile: currentBoardProfile, isProfilingBoard, refreshBoardProfile, currentBoardId } = useCanvasStore();
  const [profile, setProfile] = useState<BoardProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const isCurrentBoard = boardId === currentBoardId;

  // Fetch profile for this specific board
  useEffect(() => {
    if (!boardId) return;

    if (isCurrentBoard && currentBoardProfile) {
      setProfile(currentBoardProfile);
      setHasFetched(true);
      return;
    }

    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const { data } = await supabase
          .from('board_profiles')
          .select('*')
          .eq('board_id', boardId)
          .maybeSingle();

        if (data) {
          setProfile({
            semantic_summary: data.semantic_summary,
            key_topics: data.key_topics || [],
            item_count: data.item_count || 0,
            last_profiled_at: data.last_profiled_at,
          });
        }
      } catch (err) {
        console.error('Failed to fetch board profile:', err);
      } finally {
        setIsLoading(false);
        setHasFetched(true);
      }
    };

    fetchProfile();
  }, [boardId, isCurrentBoard, currentBoardProfile]);

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      if (isCurrentBoard) {
        // Use the store action for current board
        await refreshBoardProfile();
        const updatedProfile = useCanvasStore.getState().boardProfile;
        if (updatedProfile) setProfile(updatedProfile);
      } else {
        // Fetch items for this board and call the edge function directly
        const { data: boardItems } = await supabase
          .from('items')
          .select('*')
          .eq('board_id', boardId)
          .limit(50);

        if (boardItems && boardItems.length > 0) {
          const { generateBoardProfile } = await import('@/lib/boardProfileService');
          const newProfile = await generateBoardProfile(boardId, boardItems as any);
          if (newProfile) setProfile(newProfile);
        }
      }
    } catch (err) {
      console.error('Failed to generate profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9998]" 
        onClick={onClose}
      />
      
      {/* Popover */}
      <div 
        className="fixed z-[9999] w-80 bg-background/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl overflow-hidden"
        style={{ 
          left: Math.min(position.x, window.innerWidth - 340),
          top: Math.min(position.y, window.innerHeight - 300),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border/30 bg-muted/30">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-partnr/10">
              <Brain className="h-4 w-4 text-partnr" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{boardName}</p>
              <p className="text-[10px] text-muted-foreground">Board Profile</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              title="Refresh profile"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Analyzing board content...</span>
            </div>
          ) : !profile && hasFetched ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">No profile generated yet</p>
              <button
                onClick={handleGenerate}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-partnr/10 hover:bg-partnr/20 text-partnr text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Brain className="h-4 w-4" />
                Generate Profile
              </button>
            </div>
          ) : profile ? (
            <>
              {/* Summary */}
              {profile.semantic_summary && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {profile.semantic_summary}
                </p>
              )}

              {/* Topics */}
              {profile.key_topics && profile.key_topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {profile.key_topics.map((topic: string) => (
                    <span
                      key={topic}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-partnr/10 text-partnr font-medium"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}

              {/* Meta */}
              <div className="flex items-center justify-between text-[10px] text-muted-foreground/60 pt-2 border-t border-border/30">
                <span>{profile.item_count} items analyzed</span>
                {profile.last_profiled_at && (
                  <span>Updated {new Date(profile.last_profiled_at).toLocaleDateString()}</span>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
